// client/src/lib/cryptoService.ts

class CryptoService {
  private encoder = new TextEncoder();
  private decoder = new TextDecoder();

  // Generate random salt
  generateSalt(): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(32));
  }

  // Convert Uint8Array to base64
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  // Convert base64 to Uint8Array
  private base64ToArrayBuffer(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  // Argon2-like implementation using PBKDF2 (since Argon2 isn't natively supported)
  // Using high iterations to approximate Argon2 security
  async hashPassword(password: string, salt: Uint8Array): Promise<string> {
    const passwordBuffer = this.encoder.encode(password);
    
    // Import password as key material
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      passwordBuffer,
      { name: 'PBKDF2' },
      false,
      ['deriveBits']
    );

    // Derive key using PBKDF2 with high iterations (approximating Argon2)
    const derivedBits = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 600000, // High iteration count for security
        hash: 'SHA-256'
      },
      keyMaterial,
      256 // 32 bytes
    );

    return this.arrayBufferToBase64(derivedBits);
  }

  // Verify password against hash
  async verifyPassword(password: string, hash: string, salt: string | Uint8Array): Promise<boolean> {
    try {
      const saltBytes = typeof salt === 'string' ? this.base64ToArrayBuffer(salt) : salt;
      const computedHash = await this.hashPassword(password, saltBytes);
      return computedHash === hash;
    } catch (error) {
      console.error('Password verification failed:', error);
      return false;
    }
  }

  // Encrypt data using AES-GCM
  async encryptData(data: string, password: string): Promise<{ encrypted: string; iv: string; salt: string }> {
    const salt = this.generateSalt();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    
    // Derive key from password
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      this.encoder.encode(password),
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    );

    const key = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt']
    );

    // Encrypt data
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv },
      key,
      this.encoder.encode(data)
    );

    return {
      encrypted: this.arrayBufferToBase64(encrypted),
      iv: this.arrayBufferToBase64(iv),
      salt: this.arrayBufferToBase64(salt)
    };
  }

  // Decrypt data using AES-GCM
  async decryptData(encryptedData: string, password: string, iv: string, salt: string): Promise<string> {
    const saltBytes = this.base64ToArrayBuffer(salt);
    const ivBytes = this.base64ToArrayBuffer(iv);
    const encryptedBytes = this.base64ToArrayBuffer(encryptedData);

    // Derive key from password
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      this.encoder.encode(password),
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    );

    const key = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: saltBytes,
        iterations: 100000,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['decrypt']
    );

    // Decrypt data
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivBytes },
      key,
      encryptedBytes
    );

    return this.decoder.decode(decrypted);
  }
}

export const cryptoService = new CryptoService();